
/**
 * Optimized time utility functions for the FocusCal application
 */

import { format as formatDateFns, parse } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';

// Cache for timezone formatting to avoid repeated calculations
const timezoneFormatCache = new Map<string, string>();

export function isSlotBusy(slotStart: Date, slotEnd: Date, events: any[]): boolean {
  // Use early returns for performance
  if (!events || events.length === 0) return false;
  
  for (const event of events) {
    // Skip invalid events
    if (!event.start?.dateTime || !event.end?.dateTime) continue;
    
    const eventStart = new Date(event.start.dateTime);
    const eventEnd = new Date(event.end.dateTime);

    // Check for overlap
    if (slotStart < eventEnd && slotEnd > eventStart) {
      return true;
    }
  }
  return false;
}

// Cache for time formatting to avoid repeated calculations
const timeFormatCache = new Map<string, string>();

export function formatTimeSlot(time: string, timezone?: string): string {
  try {
    // Early return for already formatted time
    if (time.includes('AM') || time.includes('PM')) {
      return time;
    }
    
    // Use cache for repeated formatting
    const cacheKey = `${time}-${timezone || 'default'}`;
    if (timeFormatCache.has(cacheKey)) {
      return timeFormatCache.get(cacheKey)!;
    }
    
    // Create date object for the current day with the specified time
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();
    
    // Parse the time string into hours and minutes
    const [hourStr, minuteStr] = time.split(':');
    const hour = parseInt(hourStr);
    const minute = parseInt(minuteStr || '0');
    
    if (isNaN(hour) || (minuteStr && isNaN(minute))) {
      console.error(`[formatTimeSlot] Invalid time format: ${time}`);
      return time;
    }
    
    // Create a date object with the parsed time
    const date = new Date(year, month, day, hour, minute);
    
    let result: string;
    
    // Format using date-fns-tz if timezone is provided
    if (timezone) {
      const zonedDate = toZonedTime(date, timezone);
      result = formatInTimeZone(zonedDate, timezone, 'h:mm a');
    } else {
      // Otherwise use the standard date-fns formatter
      result = formatDateFns(date, 'h:mm a');
    }
    
    // Cache the result
    timeFormatCache.set(cacheKey, result);
    
    return result;
  } catch (error) {
    console.error("[formatTimeSlot] Error formatting time slot:", error);
    return time; // Return the original string if there's an error
  }
}

export function formatTimeZoneDisplay(tz: string): string {
  // Use cache for timezone formatting
  if (timezoneFormatCache.has(tz)) {
    return timezoneFormatCache.get(tz)!;
  }
  
  try {
    const now = new Date();
    const tzName = new Intl.DateTimeFormat('en', {
      timeZoneName: 'short',
      timeZone: tz
    }).formatToParts(now).find(part => part.type === 'timeZoneName')?.value || '';
    
    const result = `${tz.replace(/_/g, ' ')} (${tzName})`;
    timezoneFormatCache.set(tz, result);
    return result;
  } catch (e) {
    console.error("[formatTimeZone] Error formatting timezone:", e);
    return tz;
  }
}

export function generateFallbackTimeSlots(date: Date): { startTime: string; endTime: string; isAvailable: boolean }[] {
  console.warn("[time-utils] WARNING: This function should not be used in production!");
  
  const slots = [];
  const startHour = 9;
  const endHour = 17;
  const interval = 30; // minutes
  
  for (let hour = startHour; hour < endHour; hour++) {
    for (let minute = 0; minute < 60; minute += interval) {
      const startTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      
      const endMinute = minute + interval;
      const endHour = endMinute >= 60 ? hour + 1 : hour;
      const normalizedEndMinute = endMinute % 60;
      const endTime = `${endHour.toString().padStart(2, '0')}:${normalizedEndMinute.toString().padStart(2, '0')}`;
      
      slots.push({
        startTime,
        endTime,
        isAvailable: true
      });
    }
  }
  
  return slots;
}

// Performant function to check date equality by day (ignoring time)
export function isSameCalendarDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

// Format date for calendar display with different formats
export function formatCalendarDate(date: Date, format: 'long' | 'short' | 'weekday' = 'short'): string {
  try {
    switch (format) {
      case 'long':
        return new Intl.DateTimeFormat('en-US', { 
          weekday: 'long', 
          month: 'long', 
          day: 'numeric', 
          year: 'numeric' 
        }).format(date);
      case 'short':
        return new Intl.DateTimeFormat('en-US', { 
          month: 'short', 
          day: 'numeric'
        }).format(date);
      case 'weekday':
        return new Intl.DateTimeFormat('en-US', { 
          weekday: 'short' 
        }).format(date);
      default:
        return date.toDateString();
    }
  } catch (error) {
    console.error("[formatCalendarDate] Error formatting date:", error);
    return date.toDateString();
  }
}

// Convert 12 hour format time string to 24 hour format
export function convert12hTo24h(time12h: string): string {
  try {
    if (!time12h.includes('AM') && !time12h.includes('PM')) {
      return time12h; // Already in 24h format
    }
    
    const [timePart, period] = time12h.split(' ');
    let [hours, minutes] = timePart.split(':').map(Number);
    
    if (period === 'PM' && hours < 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  } catch (error) {
    console.error("[convert12hTo24h] Error converting time format:", error);
    return time12h;
  }
}

// Format seconds since midnight to AM/PM string (local time)
export function formatSecondsToAMPM(seconds: number | null | undefined): string {
  if (seconds == null) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const date = new Date();
  date.setHours(h, m, 0, 0);
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
}

// Circular mean for time-of-day in seconds since midnight
export function circularMeanTimeOfDay(times: number[]): number {
  if (!times.length) return 0;
  const angles = times.map(sec => (sec / 86400) * 2 * Math.PI);
  const sumSin = angles.reduce((sum, a) => sum + Math.sin(a), 0);
  const sumCos = angles.reduce((sum, a) => sum + Math.cos(a), 0);
  const meanAngle = Math.atan2(sumSin / times.length, sumCos / times.length);
  let meanSec = (meanAngle / (2 * Math.PI)) * 86400;
  if (meanSec < 0) meanSec += 86400;
  return meanSec;
}
